/* eslint-disable react/prop-types */
import { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { noop, pick, pickBy } from 'lodash';
import { useTheme } from '@material-ui/core';
import { BugReport as BugReportIcon } from '@material-ui/icons';

import AppContext from '../AppContext';
import { DevCenterRouterContext } from '../DevCenterRouterContext';

import { useAnnotationsData, useAnnotationsList, useAppMaximized, useAppSettings } from './effects';
import {
  AppActions,
  AppSettingsDialog,
  DcFullscreenElemsCoordinatorProvider,
  DevCenterAppView,
  FullscreenButton,
  NonPriorityMenus,
} from './components';
import RestoreAppSizeButton from './components/RestoreAppSizeButton';
import { getAppAvailability, getAppVersion } from './utils';
import PriorityMenus from './components/PriorityMenus';
import { RollbarWrapper } from './components/RollbarWrapper/RollbarWrapper';
import { CommentsSwitchButton, CommentsSwitchProvider } from './components/CommentsSwitch';

import styles from './DevCenterAppContainer.css';

import { isInsideNewMobileApp, isMobileDetected, isNativeDetected } from '~/utils/mobileDetect';
import AnnotationIcon from '~components/Icons/AnnotationIcon';
import { AnnotationsList, AnnotationsProvider } from '~/components/Annotations';
import { ToastContainer } from '~/components/Toaster';
import { useDevCenterApp } from '~/effects';
import { DEV_CENTER_CLI_APP_ID } from '~/constants';
import { getAppName, getAppSegment, getTimestampFromQuery } from '~/utils/devcenter';
import { showSuccessNotification } from '~/utils';
import { HelpCenterIcon } from '~/components/HelpCenter';
import { ButtonIcon, Tooltip } from '~/componentsV2';
import { AppCommonsProvider } from '~/componentsV2/AppCommonsProvider';

const isMobileView = isNativeDetected || isMobileDetected;
const PAGE_NAME = 'DevCenter_AppContainer';

const isHomePage = router => !!router?.location?.pathname?.startsWith('/home');

function DevCenterAppContainer({
  CLIAppComponent,
  CLIAppSettings,
  app,
  appId,
  className,
  coordinates,
  currentDashboardAppsLastAnnotations,
  currentUser,
  devCenterRouter,
  isActionsDisabled,
  isSettingsDialogHidden,
  layoutEnvironment,
  onAppContainerClick = noop,
  onAppRemove,
  onReportBugClick,
  onCloneDashboard,
  onHelpCenterClick,
  onIsMaximizedChange,
  onSettingChange,
  onSettingsChange,
  openIntercom = noop,
  setIsFullscreenModalMode = noop,
  timestamp,
  updateCurrentDashboardAppLastAnnotation,
  dashboardMeta,
  reloadApp = undefined,
  chatActions,
}) {
  const appsData = useContext(AppContext);
  const theme = useTheme();
  const [mainMenuItems, setMainMenuItems] = useState([]);
  const [secondaryMenuItems, setSecondaryMenuItems] = useState([]);
  const appData = appsData?.[appId || app.id] ?? {
    isLoading: true,
  };
  const application = app.package?.manifest?.application;
  const segment = getAppSegment(app);
  const isMultiRig = !!application?.ui?.multi_rig;
  const areCommentsEnabled = !!application?.ui?.enable_comments;
  const appName = getAppName(app);
  const { isMaximized, setIsMaximized } = useAppMaximized({
    onIsMaximizedChange,
    devCenterRouter,
    appId,
  });

  // Note: we need to pass timestamp directly for apps that are embedded in isolated apps
  const appTimestamp = timestamp || getTimestampFromQuery(devCenterRouter.location.query?.query);
  const isCLIApp = app.id === DEV_CENTER_CLI_APP_ID;
  const updatedCoordinates = isCLIApp && isMobileView ? { ...coordinates, w: 12 } : coordinates;
  const version = getAppVersion(app);

  const {
    isLoading: isAppLoading,
    AppSettings,
    AppComponent,
  } = useDevCenterApp({
    CLIAppComponent,
    CLIAppSettings,
    app,
    version,
  });

  const { isAppSettingsDialogOpened, toggleAppSettingsDialog } = useAppSettings({
    setIsFullscreenModalMode,
  });
  const { isAnnotationsListOpened, toggleAnnotationsList } = useAnnotationsList();

  const isLoading = isAppLoading || !currentUser || (!isMultiRig && appData.isLoading);

  const { appLastAnnotation, assetCompanyId, assetIdForAnnotation, isAppSupportsAnnotations } =
    useAnnotationsData({
      app,
      appData,
      currentDashboardAppsLastAnnotations,
    });

  const filteredAppData = pickBy(
    pick(appData, ['well', 'rig', 'fracFleet', 'wells', 'interventionUnit']),
    Boolean
  );

  const simplifiedSecondaryMenuItems = useMemo(
    () =>
      secondaryMenuItems?.map(item => ({
        ...item,
        onClick: () => {
          item.onClick?.();
          if (item.to) {
            devCenterRouter.push(item.to);
          }
        },
      })),
    [secondaryMenuItems, devCenterRouter]
  );

  const appHeadersV3Enabled = !!app?.package?.manifest?.application?.ui?.use_app_header_v3;
  const isInsideHomePage = isHomePage(devCenterRouter);

  // ⚠️⚠️⚠️ When adding/changing providers, also add them to/change /src/testing/AppTestWrapper
  return (
    <RollbarWrapper
      companyId={app?.app?.company_id}
      appKey={application?.key}
      isCLIApp={isCLIApp}
      rollbarManifestConfig={app?.package?.manifest?.settings?.rollbar}
      version={version}
    >
      <DevCenterRouterContext.Provider value={devCenterRouter}>
        <DcFullscreenElemsCoordinatorProvider setIsFullscreenModalMode={setIsFullscreenModalMode}>
          <AppCommonsProvider
            maximized={isMaximized}
            setMaximized={setIsMaximized}
            appId={app?.app?.id}
            app={app}
            appSettings={app.settings}
            onSettingChange={onSettingChange}
            onSettingsChange={onSettingsChange}
            appHeadersV3Enabled={appHeadersV3Enabled}
            currentUser={currentUser}
            appData={appData}
            layoutEnvironment={layoutEnvironment}
            appSettingsComponent={AppSettings}
            dashboardMeta={dashboardMeta}
            toggleAnnotationsList={isAppSupportsAnnotations ? toggleAnnotationsList : undefined}
            onHelpCenterClick={onHelpCenterClick}
            secondaryMenuItems={simplifiedSecondaryMenuItems}
            onAppRemove={onAppRemove}
            isInsideNewMobileApp={isInsideNewMobileApp}
            globalOffsetWells={dashboardMeta?.globalOffsetWells}
            globalOffsetWellsVersion={dashboardMeta?.globalOffsetWellsVersion}
            reloadApp={reloadApp}
            {...filteredAppData}
          >
            <CommentsSwitchProvider>
              <div
                className={classNames(
                  'dc-app-container',
                  isInsideNewMobileApp && 'dc-app-container--inside-new-mobile-app',
                  isInsideHomePage && 'dc-app-container--inside-home-page',
                  className,
                  // This class is applied so that DC apps developers can style their content
                  // when an app is in fullscreen
                  isMaximized && 'dc-app-container__maximized',
                  theme.isLightTheme && 'dc-app-container__light-theme',
                  styles.container
                )}
                data-testid={`${PAGE_NAME}_${appName}`}
                // It's important to call it without native event because it's passed through iframe
                onClick={() => onAppContainerClick()}
              >
                <ToastContainer />
                <div className={styles.containerContent}>
                  <DevCenterAppView
                    AppComponent={AppComponent}
                    app={app}
                    appData={filteredAppData}
                    isAppLoading={isAppLoading}
                    availability={getAppAvailability(
                      app.availability,
                      currentUser.company.with_subscription
                    )}
                    isCLIApp={isCLIApp}
                    isLoading={isLoading}
                    isMultiRig={isMultiRig}
                    layoutEnvironment={layoutEnvironment}
                    openIntercom={openIntercom}
                    segment={segment}
                    toggleAppSettingsDialog={toggleAppSettingsDialog}
                    appProps={{
                      coordinates: updatedCoordinates,
                      currentUser,
                      devCenterRouter,
                      onSettingChange,
                      onSettingsChange,
                      segment,
                      setIsFullscreenModalMode,
                      setIsMaximized,
                      setMainMenuItems,
                      setSecondaryMenuItems,
                      toggleAppSettingsDialog,
                      // TODO remove when componentsV2 AppHeader is used everywhere
                      appHeaderProps: {
                        app,
                        appLastAnnotation,
                        appSettings: app.settings,
                        coordinates: updatedCoordinates,
                        currentUser,
                        isMaximized,
                        layoutEnvironment,
                        onSettingChange,
                        toggleAnnotationsList,
                        updateCurrentDashboardAppLastAnnotation,
                        ...filteredAppData,
                      },
                      isNative: isNativeDetected,
                      layoutEnvironment,
                      timestamp: appTimestamp,
                      chatActions,
                    }}
                  />
                </div>
                {isAppSettingsDialogOpened && (
                  <AppSettingsDialog
                    AppSettingsComponent={AppSettings}
                    app={app}
                    appData={appData}
                    appName={appName}
                    currentUser={currentUser}
                    isMultiRig={isMultiRig}
                    isSettingsDialogHidden={isSettingsDialogHidden}
                    layoutEnvironment={layoutEnvironment}
                    onAppRemove={onAppRemove}
                    onCloneDashboard={onCloneDashboard}
                    onSettingChange={onSettingChange}
                    onSettingsChange={onSettingsChange}
                    openIntercom={openIntercom}
                    rigId={appData.rig?.id ?? null}
                    timestamp={appTimestamp}
                    toggleAppSettingsDialog={toggleAppSettingsDialog}
                  />
                )}
                {isAnnotationsListOpened && (
                  <AnnotationsProvider>
                    <AnnotationsList
                      appId={app.app.id}
                      appSettings={app.settings}
                      appTitle={appName}
                      assetCompanyId={assetCompanyId}
                      assetId={assetIdForAnnotation}
                      currentUser={currentUser}
                      onClose={toggleAnnotationsList}
                      showSuccessNotification={showSuccessNotification}
                    />
                  </AnnotationsProvider>
                )}
                {isMaximized && !appHeadersV3Enabled && (
                  <RestoreAppSizeButton setIsMaximized={setIsMaximized} />
                )}

                {!isActionsDisabled && (!appHeadersV3Enabled || isLoading) && (
                  <AppActions>
                    {areCommentsEnabled && <CommentsSwitchButton currentUser={currentUser} />}

                    {isAppSupportsAnnotations && (
                      <Tooltip title="Add Annotation" placement="top" disabled={!!isMobileView}>
                        <ButtonIcon
                          size="small"
                          type="tertiaryBg"
                          onClick={toggleAnnotationsList}
                          testId={`${PAGE_NAME}_addAnnotation`}
                        >
                          <AnnotationIcon />
                        </ButtonIcon>
                      </Tooltip>
                    )}

                    {onReportBugClick && !isCLIApp && (
                      <Tooltip title="Report A Bug" placement="top" disabled={!!isMobileView}>
                        <ButtonIcon
                          size="small"
                          type="tertiaryBg"
                          onClick={() => onReportBugClick()}
                        >
                          <BugReportIcon />
                        </ButtonIcon>
                      </Tooltip>
                    )}

                    <Tooltip
                      placement="top"
                      disabled={!!isMobileView || isCLIApp}
                      title="Help Center"
                    >
                      <ButtonIcon
                        testId={`${PAGE_NAME}_helpContent`}
                        onClick={() => onHelpCenterClick()}
                        disabled={isCLIApp}
                        size="small"
                        type="tertiaryBg"
                      >
                        <HelpCenterIcon
                          appId={app.app.id}
                          helpContentUpdatedAt={app.help_content_updated_at}
                        />
                      </ButtonIcon>
                    </Tooltip>

                    {isMobileView && !isMaximized && !isInsideNewMobileApp ? (
                      <FullscreenButton setIsMaximized={setIsMaximized} />
                    ) : (
                      <>
                        <PriorityMenus
                          menuItems={mainMenuItems}
                          devCenterRouter={devCenterRouter}
                          isMobileView={isMobileView}
                        />
                        <NonPriorityMenus
                          devCenterRouter={devCenterRouter}
                          isMaximized={isMaximized}
                          isMobileView={isMobileView}
                          menuItems={secondaryMenuItems}
                          setIsFullscreenModalMode={setIsFullscreenModalMode}
                          setIsMaximized={setIsMaximized}
                          toggleAppSettingsDialog={toggleAppSettingsDialog}
                        />
                      </>
                    )}
                  </AppActions>
                )}
              </div>
            </CommentsSwitchProvider>
          </AppCommonsProvider>
        </DcFullscreenElemsCoordinatorProvider>
      </DevCenterRouterContext.Provider>
    </RollbarWrapper>
  );
}

export default DevCenterAppContainer;
